/**
 * Dashboard Slice for LifeScribe Admin Dashboard
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DashboardOverview } from '../types/dashboard';
import apiService from '../services/api';

interface DashboardState {
  overview: DashboardOverview | null;
  revenueData: any;
  customerData: any;
  subscriptionData: any;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: DashboardState = {
  overview: null,
  revenueData: null,
  customerData: null,
  subscriptionData: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

// Async thunks
export const fetchDashboardOverview = createAsyncThunk(
  'dashboard/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiService.getDashboardOverview();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch dashboard overview');
    }
  }
);

export const fetchRevenueData = createAsyncThunk(
  'dashboard/fetchRevenue',
  async (period: '7d' | '30d' | '90d' = '30d', { rejectWithValue }) => {
    try {
      const data = await apiService.getRevenueData(period);
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch revenue data');
    }
  }
);

export const fetchCustomerData = createAsyncThunk(
  'dashboard/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiService.getCustomerData();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer data');
    }
  }
);

export const fetchSubscriptionData = createAsyncThunk(
  'dashboard/fetchSubscriptions',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiService.getSubscriptionData();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch subscription data');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLastUpdated: (state) => {
      state.lastUpdated = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard overview
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.overview = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Revenue data
      .addCase(fetchRevenueData.fulfilled, (state, action) => {
        state.revenueData = action.payload;
      })
      // Customer data
      .addCase(fetchCustomerData.fulfilled, (state, action) => {
        state.customerData = action.payload;
      })
      // Subscription data
      .addCase(fetchSubscriptionData.fulfilled, (state, action) => {
        state.subscriptionData = action.payload;
      });
  },
});

export const { clearError, setLastUpdated } = dashboardSlice.actions;
export default dashboardSlice.reducer;